import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Layout, { LAYOUT_TEMPLATE_COLUMNS } from 'components/atoms/Layout';
import Logo, { LOGO_VARIANTS } from 'components/atoms/Logo';
import Button from 'components/atoms/Button';
import { GlobalStyle } from 'theme/emotion';
import LandingIllustration from 'assets/landing-hero.png';
import { ROUTES } from 'navigation/constants';
import { signUpWithApple, signUpWithGoogle } from 'features/Registration/actions';
import type { RegistrationFormActions } from 'features/Registration/types';
import { useRemoteConfig } from 'features/RemoteConfig/queries';
import { isAndroid, isIOS, isIOSInAppBrowser } from 'utils/constants';

const LandingLayout = styled(Layout)`
  ${({ theme: { colors, utils, mq } }) => css`
    min-height: 100vh;
    padding: ${utils.rem(32)} 0 0;
    color: ${colors.neutral.white};
    background:
      radial-gradient(130% 120% at 50% -10%, ${utils.alphaHex(colors.primary.lightDark, 35)} 0%, transparent 65%),
      linear-gradient(180deg, ${colors.primary.dark} 0%, ${colors.primary.main} 90%, ${colors.primary.main} 100%);
    overflow: hidden;

    ${mq.md.up} {
      padding: ${utils.rem(48)} 0 0;
    }

    &::before {
      content: '';
      position: absolute;
      top: ${utils.rem(-140)};
      left: 50%;
      width: ${utils.rem(520)};
      height: ${utils.rem(520)};
      border-radius: 50%;
      background: radial-gradient(circle, ${utils.alphaHex(colors.secondary.light, 45)} 0%, transparent 75%);
      filter: blur(${utils.rem(140)});
      transform: translateX(-50%);
      z-index: 0;
    }

    > header,
    > main {
      position: relative;
      z-index: 1;
      width: 100%;
      max-width: ${utils.rem(360)};
      padding: 0 ${utils.rem(16)};
      margin: 0 auto;
    }

    > footer {
      grid-column: ${LAYOUT_TEMPLATE_COLUMNS.gridStart} / ${LAYOUT_TEMPLATE_COLUMNS.gridEnd};
    }
  `}
`;

const Header = styled.header`
  ${({ theme: { utils } }) => css`
    display: flex;
    justify-content: center;
    margin-bottom: ${utils.rem(16)};
  `}
`;

const StyledLogo = styled(Logo)`
  ${({ theme: { utils } }) => css`
    height: ${utils.rem(28)};
    width: auto;
  `}
`;

const Main = styled.main`
  ${({ theme: { utils } }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${utils.rem(32)};
  `}
`;

const ContentWrapper = styled.div`
  ${({ theme: { utils } }) => css`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: ${utils.rem(32)};
  `}
`;

const HeroSection = styled.section`
  ${({ theme: { utils } }) => css`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${utils.rem(24)};
  `}
`;

const HeroFigure = styled.div`
  ${({ theme: { utils } }) => css`
    position: relative;
    width: 100%;
    display: flex;
    justify-content: center;
    padding-bottom: ${utils.rem(12)};
  `}
`;

const HeroBackdrop = styled.div`
  ${({ theme: { colors, utils } }) => css`
    position: absolute;
    inset: 0;
    display: flex;
    justify-content: center;
    pointer-events: none;

    &::before,
    &::after {
      content: '';
      position: absolute;
      border-radius: ${utils.rem(240)};
      filter: blur(${utils.rem(2)});
    }

    &::after {
      width: ${utils.rem(260)};
      height: ${utils.rem(160)};
      top: ${utils.rem(20)};
      left: 50%;
      transform: translateX(-50%);
      background: radial-gradient(70% 70% at 50% 40%, ${utils.alphaHex(colors.neutral.white, 18)} 0%, transparent 70%);
    }
  `}
`;

const HeroImage = styled.img`
  ${({ theme: { utils } }) => css`
    position: relative;
    z-index: 1;
    width: 88%;
    max-width: ${utils.rem(280)};
    display: block;
    filter: drop-shadow(0 ${utils.rem(20)} ${utils.rem(40)} rgba(0, 0, 0, 0.35));
  `}
`;

const Content = styled.section`
  ${({ theme: { utils } }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: ${utils.rem(12)};
  `}
`;

const Badge = styled.span`
  ${({ theme: { utils, colors, tp } }) => css`
    ${tp.p3}
    font-size: ${utils.rem(11)};
    letter-spacing: ${utils.rem(1.5)};
    text-transform: uppercase;
    border-radius: ${utils.rem(999)};
    padding: ${utils.rem(6)} ${utils.rem(18)};
    border: ${utils.rem(1)} solid ${utils.alphaHex(colors.neutral.white, 25)};
    background-color: ${utils.alphaHex(colors.neutral.white, 6)};
    color: ${colors.neutral.white};
  `}
`;

const Title = styled.h1`
  ${({ theme: { tp, colors } }) => css`
    ${tp.h3}
    margin: 0;
    color: ${colors.neutral.white};
  `}
`;

const Subtitle = styled.p`
  ${({ theme: { tp, colors, utils } }) => css`
    ${tp.p3}
    margin: 0;
    color: ${utils.alphaHex(colors.neutral.white, 75)};
  `}
`;

const Ctas = styled.div`
  ${({ theme: { utils } }) => css`
    display: flex;
    flex-direction: column;
    gap: ${utils.rem(14)};
    width: 100%;
  `}
`;

const PrimaryButton = styled(Button)`
  ${({ theme: { utils } }) => css`
    width: 100%;
    min-height: ${utils.rem(52)};
    font-size: ${utils.rem(16)};
    border-radius: ${utils.rem(24)};
  `}
`;

const SecondaryButton = styled(PrimaryButton)`
  ${({ theme: { utils } }) => css`
    min-height: ${utils.rem(52)};
  `}
`;

// Apple Sign In button styled per Apple Human Interface Guidelines
const AppleButton = styled(PrimaryButton)`
  ${({ theme: { colors, utils } }) => css`
    background-color: ${colors.neutral.black};
    color: ${colors.neutral.white};
    border-color: ${colors.neutral.black};

    &:hover {
      background-color: ${utils.alphaHex(colors.neutral.black, 85)};
    }

    &:active {
      background-color: ${utils.alphaHex(colors.neutral.black, 75)};
    }

    &:disabled {
      opacity: 0.5;
    }
  `}
`;

const Footer = styled.footer`
  ${({ theme: { utils } }) => css`
    width: 100%;
    margin: 0;
    padding: 0;
  `}
`;

const CtaSection = styled.div`
  ${({ theme: { colors, utils } }) => css`
    position: sticky;
    bottom: 0;
    z-index: 2;
    width: 100%;
    background: ${colors.neutral.white};
    border-radius: ${utils.rem(32)} ${utils.rem(32)} 0 0;
    box-shadow: 0 ${utils.rem(-4)} ${utils.rem(32)} ${utils.alphaHex(colors.primary.dark, 25)};
    padding: ${utils.rem(32)} ${utils.rem(24)} calc(${utils.rem(24)} + env(safe-area-inset-bottom, 0px));
    color: ${colors.primary.main};
  `}
`;

const CtaContent = styled.div`
  ${({ theme: { utils } }) => css`
    width: 100%;
    max-width: ${utils.rem(420)};
    margin: 0 auto;
    text-align: center;
  `}
`;

const TermsText = styled.p`
  ${({ theme: { tp, colors, utils } }) => css`
    ${tp.p3}
    font-size: ${utils.rem(12)};
    margin: ${utils.rem(12)} 0 0;
    color: ${utils.alphaHex(colors.primary.main, 55)};
  `}
`;

const TermsLink = styled.a`
  ${({ theme: { colors } }) => css`
    color: ${colors.primary.light};
    text-decoration: underline;
  `}
`;

const OAuthCta = styled.div`
  ${({ theme: { utils } }) => css`
    display: flex;
    flex-direction: column;
    gap: ${utils.rem(6)};
  `}
`;

const ErrorMessage = styled.span`
  ${({ theme: { utils, colors } }) => css`
    font-size: ${utils.rem(13)};
    color: ${colors.error.main};
    text-align: center;
  `}
`;

const Landing = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const rawEmail = searchParams.get('email') ?? '';
  const email = rawEmail.replace(/ /g, '+');
  const web2wave_user_id = searchParams.get('web2wave_user_id') ?? '';
  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);
  const [googleError, setGoogleError] = useState('');
  const [isAppleSubmitting, setIsAppleSubmitting] = useState(false);
  const [appleError, setAppleError] = useState('');

  const searchParamsString = searchParams.toString();
  const { data: remoteConfig } = useRemoteConfig();

  const forwardedQuery = useMemo(() => {
    const params = new URLSearchParams(searchParamsString);
    if (rawEmail) {
      params.set('email', email);
    }
    return params.toString();
  }, [searchParamsString, rawEmail, email]);

  const googleActions = useMemo<RegistrationFormActions>(
    () => ({
      setSubmitting: value => {
        setIsGoogleSubmitting(value);
        if (value) {
          setGoogleError('');
        }
      },
      setFieldError: (field, message) => {
        if (field === 'form') {
          setGoogleError(message ?? '');
        }
      },
    }),
    [setIsGoogleSubmitting, setGoogleError]
  );

  const appleActions = useMemo<RegistrationFormActions>(
    () => ({
      setSubmitting: value => {
        setIsAppleSubmitting(value);
        if (value) {
          setAppleError('');
        }
      },
      setFieldError: (field, message) => {
        if (field === 'form') {
          setAppleError(message ?? '');
        }
      },
    }),
    [setIsAppleSubmitting, setAppleError]
  );

  // Disable Google sign-in on iOS in-app browsers (Instagram, Facebook) where it doesn't work properly
  const shouldShowGoogleCta = Boolean(
    !isIOSInAppBrowser && (
      (remoteConfig?.isContinueWithGoogleEnabledIOS && isIOS)
      || (remoteConfig?.isContinueWithGoogleEnabledAndroid && isAndroid)
      || (remoteConfig?.isContinueWithGoogleEnabledOther && !isIOS && !isAndroid)
    )
  );

  // Apple sign-in is only available on iOS
  const shouldShowAppleCta = Boolean(
    remoteConfig?.isContinueWithAppleEnabledIOS && isIOS
  );

  const handleRegistration = () => {
    const destination = forwardedQuery
      ? `${ROUTES.REGISTRATION.path}?${forwardedQuery}`
      : ROUTES.REGISTRATION.path;
    navigate(destination);
  };

  const handleContinueWithGoogle = () => {
    dispatch(signUpWithGoogle({ actions: googleActions, web2wave_user_id }));
  };

  const handleContinueWithApple = () => {
    dispatch(signUpWithApple({ actions: appleActions, web2wave_user_id }));
  };

  return (
    <>
      <GlobalStyle />
      <LandingLayout>
        <Header>
          <StyledLogo variant={LOGO_VARIANTS.named} light />
        </Header>

        <Main>
          <ContentWrapper>
            <HeroSection>
              <HeroFigure>
                <HeroBackdrop />
                <HeroImage src={LandingIllustration} alt={t('landing.heroAlt')} />
              </HeroFigure>

              <Content>
                <Badge>{t('landing.poweredBy')}</Badge>
                <Title>{t('landing.title')}</Title>
                <Subtitle>{t('landing.subtitle')}</Subtitle>
              </Content>
            </HeroSection>
          </ContentWrapper>
        </Main>

        <Footer>
          <CtaSection>
            <CtaContent>
              <Ctas>
                {shouldShowAppleCta && (
                  <OAuthCta>
                    <AppleButton onClick={handleContinueWithApple} disabled={isAppleSubmitting}>
                      {t('landing.continueWithApple')}
                    </AppleButton>
                    {appleError && <ErrorMessage>{appleError}</ErrorMessage>}
                  </OAuthCta>
                )}
                {shouldShowGoogleCta && (
                  <OAuthCta>
                    <PrimaryButton color="primary" onClick={handleContinueWithGoogle} disabled={isGoogleSubmitting}>
                      {t('landing.continueWithGoogle')}
                    </PrimaryButton>
                    {googleError && <ErrorMessage>{googleError}</ErrorMessage>}
                  </OAuthCta>
                )}
                <SecondaryButton color="primary" variant="outlined" onClick={handleRegistration}>
                  {t('landing.continueWithEmail')}
                </SecondaryButton>
              </Ctas>

              <TermsText>
                {t('landing.termsText')}{' '}
                <TermsLink href="https://leyaai.com/terms-conditions" target="_blank" rel="noopener noreferrer">
                  {t('landing.terms')}
                </TermsLink>{' '}
                {t('landing.and')}{' '}
                <TermsLink href="https://leyaai.com/privacy-policy/" target="_blank" rel="noopener noreferrer">
                  {t('landing.privacyPolicy')}
                </TermsLink>
                .
              </TermsText>
            </CtaContent>
          </CtaSection>
        </Footer>
      </LandingLayout>
    </>
  );
};

export default Landing;
